import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { ReactComponent as ChatbotReportsIcon } from "../../../assets/ChatbotReportsSvgs/ChatbotReportsIcon.svg";
import { ReactComponent as CloseChatbotReportsIcon } from "../../../assets/ChatbotReportsSvgs/CloseChatbotReportsIcon.svg";
import { ReactComponent as SendButtonIcon } from "../../../assets/ChatbotReportsSvgs/SendButtonIconChatbot.svg";
import { ReactComponent as ChatbotWindowCloseIcon } from "../../../assets/ChatbotReportsSvgs/ChatbotWindowCloseIcon.svg";
import { ReactComponent as ExpandChatbotIcon } from "../../../assets/ChatbotReportsSvgs/ExpandChatbotIcon.svg";
import { ReactComponent as MaghilChatbotLogo } from "../../../assets/ChatbotReportsSvgs/MaghilChatbotLogo.svg";
import { ReactComponent as CollapseChatbotIcon } from "../../../assets/ChatbotReportsSvgs/CollapseChatbotIcon.svg";
import { ReactComponent as ChatHistoryIcon } from "../../../assets/ChatbotReportsSvgs/ChatHistoryIcon.svg";
import { ReactComponent as BackButtonChatbotIcon } from "../../../assets/ChatbotReportsSvgs/BackButtonChatbotIcon.svg";
import UserQueryMessageBubble from './UserQueryMessageBubble';
import { usePCChatbot } from '../hooks/usePCChatbot';
import { useSpeechToText } from '../hooks/useSpeechToText';
import { PCMessageView } from '../components/PCMessageView';
import { PCActivityLogPanel } from '../components/PCActivityLogPanel';
import { PCIntro } from '../components/PCIntro';
import { PCThreadRow } from '../components/PCThreadRow';
import { Icon as BrisqueIcon } from '../components/atoms';
import { useBranch } from '../../../context/BranchContext';
import './style.scss'
import { ChatbotReportsPropsInterface } from '../../../interface/ReportsChatbotInterface';

const generateId = () => `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

function useMediaQuery(query: string): boolean {
    const [matches, setMatches] = useState(() =>
        typeof window !== 'undefined' ? window.matchMedia(query).matches : false
    );
    useEffect(() => {
        const mql = window.matchMedia(query);
        const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
        mql.addEventListener('change', handler);
        setMatches(mql.matches);
        return () => mql.removeEventListener('change', handler);
    }, [query]);
    return matches;
}

/**
 * Product Catalog Control Center AI chatbot (Brisque AI).
 *
 * Catalogue-only: every message is sent to the Brisque backend
 * (`POST /conversations/:id/messages`) which returns the message stream (§5).
 * All state lives in the `ProductCatalogChatbot` Redux slice, driven through the
 * `usePCChatbot` hook. This chatbot no longer talks to the reports backend.
 */
const ProductCatalogAIChatbot = ({
    headerTextReportsChatBot = "Product Catalog AI",
    centerTextReportsChatBot = "Ask about item availability, 86/OOS, channels and sessions.",
    messageInputPlaceholderReportsChatBot = "Ask me anything...",
    footerTextReportsChatBot = "AI may make mistakes, Check your important info",
    enableVoiceInputReportsChatBot = true,
    enableFileUploadReportsChatBot = false,
    chatbotReportsName = "Product Catalog AI",
    defaultOpen = false,
    onClose,
}: ChatbotReportsPropsInterface) => {
    const chat = usePCChatbot();
    const { selectedBranch } = useBranch();

    const locationLabel = useMemo(
        () => selectedBranch?.locationName ?? selectedBranch?.name ?? '',
        [selectedBranch]
    );
    const city = locationLabel?.split(', ')[1] || locationLabel;

    const isMobile = useMediaQuery('(max-width: 768px)');
    // The mobile header inverts to a white bar (style.scss), so the header actions can't
    // stay white or they vanish into it — match the grey the history/close icons use there.
    const headerIconColor = isMobile ? '#6F6F6F' : '#fff';

    const [isChatbotWindowExpanded, setIsChatbotWindowExpanded] = useState<boolean>(false);
    const [isChatbotWindowOpen, setIsChatbotWindowOpen] = useState<boolean>(defaultOpen);
    const [inputValue, setInputValue] = useState<string>("");
    const [activityOpen, setActivityOpen] = useState<boolean>(false);
    const [historyView, setHistoryView] = useState<'none' | 'listing'>('none');
    const [dismissedError, setDismissedError] = useState<string | null>(null);
    // Brief "Updated" confirmation shown right after a manual data refresh completes.
    const [justRefreshed, setJustRefreshed] = useState<boolean>(false);

    // Voice input (§9.3): dictation lands as editable text in the composer.
    const voice = useSpeechToText((transcript) => setInputValue(transcript));
    const scrollBottomRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    // Auto-grow the composer with its content (up to the CSS max-height, after which
    // it scrolls internally) — reset to a single row when cleared. Runs on every value
    // change, including programmatic ones (voice dictation, send-clears the box).
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }, [inputValue]);

    const conversation = chat.state.conversation;
    const conversationId = chat.state.activeConversationId;
    const messages = conversation?.messages ?? [];
    const hasUserMessage = messages.some((m) => m.kind === 'user');
    // `syncing` spans the whole trigger → countdown → results window (the saga now owns it
    // end to end) — a message sent mid-sync would race the pending catalogue refresh, so the
    // composer stays blocked exactly like it does for sending/card actions.
    const isWaitingForResponse = chat.state.sending || chat.state.cardActionLoading || chat.state.syncing;

    const iconColor = inputValue?.length > 0 ? '#E52333' : isWaitingForResponse ? '#999' : '#a8a8a8';

    // On open: resume/create the active thread + load channels and the sidebar list.
    useEffect(() => {
        if (!isChatbotWindowOpen || !chat.ready) return;
        if (!chat.state.channelsLoaded) chat.fetchChannels();
        if (!chat.state.ctasLoaded) chat.fetchCtas();
        chat.listConversations();
        if (!chat.state.activeConversationId && !chat.state.createLoading) {
            chat.createConversation();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isChatbotWindowOpen, chat.ready]);

    // Auto-scroll to bottom on new messages, but not on the intro screen.
    useEffect(() => {
        if (!hasUserMessage) return;
        scrollBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages.length, hasUserMessage]);

    const handleSendMessage = useCallback((text?: string) => {
        const messageText = (text ?? inputValue).trim();
        if (!messageText || isWaitingForResponse) return;
        if (!conversationId) {
            // Thread not ready yet — create it; the user can resend once it lands.
            if (chat.ready && !chat.state.createLoading) chat.createConversation();
            return;
        }
        // Optimistic user bubble (§5.1) — the server response returns only bot messages.
        chat.appendLocalMessage({ kind: 'user', id: generateId(), text: messageText });
        chat.sendMessage(conversationId, messageText);
        setInputValue("");
    }, [inputValue, isWaitingForResponse, conversationId, chat]);

    // Show a brief "Updated" tick when a manual refresh lands, then fade it out.
    useEffect(() => {
        if (!chat.state.catalogueRefreshedAt) return;
        setJustRefreshed(true);
        const t = setTimeout(() => setJustRefreshed(false), 2200);
        return () => clearTimeout(t);
    }, [chat.state.catalogueRefreshedAt]);

    // A successful undo from the activity log routes the user to the chat it was written into —
    // the backend appends an "Undone: …" bubble plus a fresh sync offer there, and that thread is
    // where the follow-up (syncing the reversal) happens. A REJECTED undo (`ok: false`) never
    // sets this, so the user stays in the activity log with the reason on screen. One effect on
    // the single shell, so it behaves identically on mobile and desktop.
    useEffect(() => {
        if (!chat.state.undoNavConversationId) return;
        setActivityOpen(false);
        setHistoryView('none');
        chat.clearUndoNav();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chat.state.undoNavConversationId]);

    const handleRefreshData = useCallback(() => {
        if (chat.state.refreshing || !chat.ready) return;
        chat.refreshCatalogue();
    }, [chat]);

    const handleNewChat = useCallback(() => {
        if (voice.listening) voice.stop();
        setActivityOpen(false);
        setHistoryView('none');
        setInputValue('');
        if (chat.ready) chat.createConversation(true);
    }, [chat, voice]);

    // Enter sends; Shift+Enter (and Ctrl/Cmd+Enter) inserts a newline — the modern
    // multiline-chat convention. `onKeyDown` (not `onKeyPress`, which is deprecated and
    // doesn't reliably expose the shift modifier on all browsers for textareas).
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <div className="product-catalog-ai-chatbot">
            {!defaultOpen && (
                <div className="toggle-chatbot-window-container">
                    {!isChatbotWindowOpen ? (
                        <div className="open-chatbot-window">
                            <ChatbotReportsIcon onClick={() => setIsChatbotWindowOpen(true)} />
                        </div>
                    ) : (
                        <div className="close-chatbot-window">
                            <CloseChatbotReportsIcon onClick={() => setIsChatbotWindowOpen(false)} />
                        </div>
                    )}
                </div>
            )}
            {isChatbotWindowOpen ? (
                <div className={`reports-chatbot-container ${isChatbotWindowExpanded ? 'expanded' : ''}`}>
                    <div className='reports-chatbot-header-container'>
                        <div className='maghil-insights-logo-and-text-container'>
                            {historyView === 'listing' ? (
                                <>
                                    <BackButtonChatbotIcon className='back-btn-maghil-chatbot' onClick={() => setHistoryView('none')} />
                                    <div className='maghil-insights-text'>Your chats</div>
                                </>
                            ) : (
                                <>
                                    <MaghilChatbotLogo className="maghil-chatbot-logo" />
                                    <div className='maghil-insights-text'>{headerTextReportsChatBot}</div>
                                </>
                            )}
                        </div>
                        <div className='expand-minimize-close-buttons-container'>
                            {historyView === 'none' && (
                                <span role="button"
                                    title={chat.state.refreshing ? 'Updating latest data…' : justRefreshed ? 'Data updated' : 'Update latest data — pull the newest menu state if you just changed something in the PC app'}
                                    onClick={handleRefreshData}
                                    style={{ display: 'inline-flex', cursor: chat.state.refreshing ? 'default' : 'pointer', color: headerIconColor, marginRight: 2, opacity: chat.state.refreshing ? 0.85 : 1 }}>
                                    <style>{`@keyframes briskSpin{to{transform:rotate(360deg)}}`}</style>
                                    <BrisqueIcon
                                        name={justRefreshed ? 'check_circle' : 'refresh'}
                                        size={20}
                                        color={headerIconColor}
                                        style={chat.state.refreshing ? { animation: 'briskSpin 0.8s linear infinite' } : undefined}
                                    />
                                </span>
                            )}
                            {historyView === 'none' && (
                                <span role="button" title="New chat" onClick={handleNewChat}
                                    style={{ display: 'inline-flex', cursor: 'pointer', color: headerIconColor, marginRight: 2 }}>
                                    <BrisqueIcon name="add_comment" size={20} color={headerIconColor} />
                                </span>
                            )}
                            {historyView === 'none' && (
                                <span role="button" title="Activity log — changes you've made"
                                    onClick={() => { setActivityOpen((v) => !v); setHistoryView('none'); }}
                                    style={{ display: 'inline-flex', cursor: 'pointer', color: headerIconColor, marginRight: 2, opacity: activityOpen ? 1 : 0.92 }}>
                                    <BrisqueIcon name="receipt_long" size={20} color={headerIconColor} />
                                </span>
                            )}
                            <ChatHistoryIcon className='chatbot-history-icon'
                                onClick={() => { setActivityOpen(false); setHistoryView('listing'); chat.listConversations(); }} />
                            {!isChatbotWindowExpanded && !isMobile ?
                                <ExpandChatbotIcon onClick={() => setIsChatbotWindowExpanded(true)} /> : !isMobile ?
                                    <CollapseChatbotIcon onClick={() => setIsChatbotWindowExpanded(false)} /> : <></>}
                            <ChatbotWindowCloseIcon className="chatbot-window-close-icon"
                                onClick={() => defaultOpen ? onClose?.() : setIsChatbotWindowOpen(false)} />
                        </div>
                    </div>
                    {chat.state.error && chat.state.error !== dismissedError && (
                        // Errors (e.g. a failed delete/rename that 404s server-side) were
                        // previously silent — nothing rendered chat.state.error anywhere, so a
                        // failed action just looked like it did nothing. Dismissible; a NEW
                        // error (different message) always reappears even if the last one was
                        // dismissed, since dismissal only remembers that exact message.
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '9px 16px', background: 'rgba(220,38,38,0.08)', color: 'var(--red, #DC2626)', fontSize: 12.5, fontFamily: 'var(--font-primary)', flexShrink: 0 }}>
                            <BrisqueIcon name="error" size={15} color="var(--red, #DC2626)" style={{ marginTop: 1, flexShrink: 0 }} />
                            <div style={{ flex: 1 }}>{chat.state.error}</div>
                            <button type="button" onClick={() => setDismissedError(chat.state.error)} title="Dismiss" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--red, #DC2626)', display: 'flex', flexShrink: 0 }}>
                                <BrisqueIcon name="close" size={15} />
                            </button>
                        </div>
                    )}
                    <div className='reports-chatbot-body-container'>
                        {historyView === 'listing' && (
                            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0, background: 'var(--surface)' }}>
                                {chat.state.conversations.length === 0 ? (
                                    <div style={{ padding: '40px 28px', textAlign: 'center', color: 'var(--t3)', fontSize: 13 }}>
                                        {chat.state.conversationsLoading ? 'Loading your chats…' : 'No chats yet.'}
                                    </div>
                                ) : (
                                    chat.state.conversations.map((c) => (
                                        <PCThreadRow
                                            key={c.conversationId}
                                            thread={c}
                                            onOpen={() => { chat.loadConversation(c.conversationId); setHistoryView('none'); }}
                                            onRename={(title) => chat.rename(c.conversationId, title)}
                                            onDelete={() => chat.deleteThread(c.conversationId)}
                                        />
                                    ))
                                )}
                            </div>
                        )}
                        {activityOpen && historyView === 'none' && (
                            <PCActivityLogPanel chat={chat} onClose={() => setActivityOpen(false)} />
                        )}
                        <div className="chat-body-scroll-area" style={{ display: (historyView !== 'none' || activityOpen) ? 'none' : undefined }}>
                            {!chat.ready ? (
                                <div className="chatbot-body-initial-state">
                                    <ChatbotReportsIcon />
                                    <div className='chatbot-body-text'>Select a location to start using the Product Catalog assistant.</div>
                                </div>
                            ) : chat.state.conversationLoading || chat.state.createLoading ? (
                                // Switching to a different thread (conversationLoading) or starting a
                                // fresh one (createLoading) — a brief interstitial instead of silently
                                // swapping the message list, which would otherwise flash the PREVIOUS
                                // thread's messages until the new ones land.
                                <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 9, color: 'var(--t3)', fontSize: 13 }}>
                                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--accent)', display: 'inline-block' }} />
                                    {chat.state.createLoading ? 'Starting a new chat…' : 'Loading chat…'}
                                </div>
                            ) : !hasUserMessage ? (
                                <div className="chat-messages-container">
                                    {locationLabel && <div className="date-separator">{city}</div>}
                                    <PCIntro onAsk={handleSendMessage} ctas={chat.state.ctas} />
                                    {/* Render any resumed non-user messages below the intro */}
                                    {messages.map((msg) => msg.kind === 'user' ? null : (
                                        <div key={msg.id} style={{ width: '100%', margin: '2px 0' }}>
                                            <PCMessageView msg={msg} chat={chat} conversationId={conversationId ?? ''} onSend={handleSendMessage} />
                                        </div>
                                    ))}
                                    <div ref={scrollBottomRef} />
                                </div>
                            ) : (
                                <div className="chat-messages-container">
                                    <div className="date-separator">{city}</div>
                                    {messages.map((msg) => {
                                        if (msg.kind === 'user') {
                                            return <UserQueryMessageBubble key={msg.id} text={msg.text} />;
                                        }
                                        return (
                                            <div key={msg.id} style={{ width: '100%', margin: '2px 0' }}>
                                                <PCMessageView msg={msg} chat={chat} conversationId={conversationId ?? ''} onSend={handleSendMessage} />
                                            </div>
                                        );
                                    })}
                                    {isWaitingForResponse && !chat.state.syncing && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', color: 'var(--t3)', fontSize: 12.5, fontFamily: 'var(--font-primary)' }}>
                                            <BrisqueIcon name="auto_awesome" size={15} color="var(--accent)" /> {chat.state.thinkingStatus ?? 'Thinking…'}
                                        </div>
                                    )}
                                    <div ref={scrollBottomRef} />
                                </div>
                            )}
                        </div>

                        <div className="suggestion-and-input-container" style={{ marginTop: 'auto', display: (historyView !== 'none' || activityOpen) ? 'none' : undefined }}>
                            <div className={`message-input-box-container-reports-chatbot${isWaitingForResponse ? ' is-disabled' : ''}`}>
                                <style>{`@keyframes briskMicPulse{0%,100%{box-shadow:0 0 0 0 rgba(229,35,51,0.45)}50%{box-shadow:0 0 0 6px rgba(229,35,51,0)}}`}</style>
                                <textarea
                                    ref={inputRef}
                                    rows={1}
                                    placeholder={voice.listening ? 'Listening… speak now' : (isWaitingForResponse ? 'Waiting for response...' : messageInputPlaceholderReportsChatBot)}
                                    value={inputValue}
                                    onChange={(e) => { setInputValue(e.target.value); if (voice.error) voice.clearError(); }}
                                    onKeyDown={handleKeyDown}
                                    disabled={isWaitingForResponse}
                                />
                                {enableVoiceInputReportsChatBot && voice.supported && (
                                    <span role="button" title={voice.listening ? 'Stop listening' : 'Speak your request'}
                                        onClick={() => { if (voice.listening) voice.stop(); else voice.start(); }}
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            width: 30, height: 30, borderRadius: '50%', cursor: 'pointer', flexShrink: 0,
                                            color: voice.listening ? '#fff' : '#a8a8a8',
                                            background: voice.listening ? '#E52333' : 'transparent',
                                            animation: voice.listening ? 'briskMicPulse 1.3s infinite' : 'none',
                                            transition: 'background .15s, color .15s',
                                        }}>
                                        <BrisqueIcon name={voice.listening ? 'stop' : 'mic'} size={18} color={voice.listening ? '#fff' : '#a8a8a8'} />
                                    </span>
                                )}
                                <SendButtonIcon className="send-btn-icon" onClick={() => handleSendMessage()}
                                    style={{ opacity: isWaitingForResponse ? 0.6 : 1, pointerEvents: isWaitingForResponse ? 'none' : 'auto', color: iconColor }} />
                            </div>
                            <div className="footer-chatbot-container" style={voice.error ? { color: 'var(--orange, #D97706)' } : undefined}>
                                {voice.error || (voice.listening ? 'Listening… tap the mic again to stop, then review before sending.' : footerTextReportsChatBot)}
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
};

export default ProductCatalogAIChatbot;
